"use client";

/**
 * WrappedEyebrow — top-of-card kicker for Wrapped overlays.
 *
 * Mirrors the `✦  TEXT  ✦` eyebrow that plan/niche Satori PNG share-cards
 * render via `function Eyebrow` (see route.tsx). Pulled out as a live React
 * component so brand-v2 (which renders cards as DOM nodes, not PNG images)
 * can use the same kicker shape — uppercase, wide tracking, accent-tinted,
 * sparkle-flanked — without forking the visual language across artifacts.
 *
 * Why a separate component instead of inline className: the PNG-side eyebrow
 * is the canonical reference, and brand currently has 5 cards that all need
 * the same shape. One source of truth keeps spacing/sparkle/color logic in
 * one place if we later tune it (e.g. swap ✦ for · or change tracking).
 */

import { cn } from "@/lib/utils";

interface WrappedEyebrowProps {
  /** Visible kicker text — rendered uppercase regardless of input casing. */
  text: string;
  /**
   * Tinting color (hex with leading #). The eyebrow uses `${accentHex}c0`
   * (75% alpha) for the text and the sparkle ornaments — same recipe as
   * the PNG eyebrow's `${accentHex}c0`. Defaults to amber-300-ish.
   */
  accentHex?: string;
  /** Override for full-bleed cards where the bg color requires darker fg. */
  forceColor?: string;
  /** Extra classes on the wrapper (rare). */
  className?: string;
}

const DEFAULT_ACCENT_HEX = "#FCD34D"; // amber-300, matches the brand-v2 default

export function WrappedEyebrow({
  text,
  accentHex,
  forceColor,
  className,
}: WrappedEyebrowProps) {
  const tint = forceColor ?? `${accentHex ?? DEFAULT_ACCENT_HEX}c0`;
  return (
    <div
      className={cn(
        "flex items-center justify-center gap-2",
        "font-heading text-[13px] font-semibold uppercase tracking-[0.4em]",
        className,
      )}
      style={{ color: tint }}
    >
      {/* Bookend bars — matches the visual the niche/plan PNG eyebrow ends
       *  up with (Satori has `✦  TEXT  ✦` in source but Inter doesn't ship
       *  that glyph, so the renderer falls back to a vertical-rectangle
       *  block. Using `▮` here in DOM keeps brand visually consistent
       *  with the PNG cards). */}
      <span aria-hidden>▮</span>
      <span>{text}</span>
      <span aria-hidden>▮</span>
    </div>
  );
}
